/**
 * Care→Practice Anfragen — Care Service (Sendeseite)
 *
 * Pflegeeinrichtung erstellt, verwaltet und verfolgt Anfragen an Arztpraxen.
 * Supabase-Tabelle: care_practice_requests
 */
import { supabase, isSupabaseConfigured, requireSupabase, ensureValidSession } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { getPartners } from "./network.service";
import {
    buildCarePracticeRequestStats,
    createCarePracticeRequestSubscription,
    mapCarePracticeRequestRow,
    mapTimelineEntry,
} from "../_vendored-shared/web/care-practice-request-service";
import type {
    CarePracticeRequest,
    CreateRequestInput,
    RequestStats,
    RequestStatus,
    RequestCategory,
    RequestTimelineEntry,
} from "@/types/care-practice-request";

type RealtimeDebugEntry = Record<string, unknown>;

function recordRealtimeDebug(entry: RealtimeDebugEntry): void {
    if (typeof window === "undefined") return;
    const target = window as typeof window & {
        __MFRealtimeDebug?: RealtimeDebugEntry[];
    };
    const nextEntry = { ...entry, ts: new Date().toISOString() };
    target.__MFRealtimeDebug = [...(target.__MFRealtimeDebug ?? []).slice(-49), nextEntry];
}

function getSingleRpcRecord(data: unknown): Record<string, unknown> | null {
    if (!data) return null;
    if (Array.isArray(data)) {
        const [first] = data;
        return (first as Record<string, unknown> | undefined) ?? null;
    }
    return data as Record<string, unknown>;
}

async function ensureAuthenticatedOptionRead(): Promise<void> {
    const accessToken = await ensureValidSession();
    if (!accessToken) {
        throw new Error("Sitzung abgelaufen. Bitte erneut anmelden.");
    }
}

// ── Queries ─────────────────────────────────────────────────

/** Neue Anfrage erstellen */
export async function createRequest(
    input: CreateRequestInput,
    _userId: string,
): Promise<CarePracticeRequest | null> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("create_care_practice_request", {
            p_facility_id: input.facilityId ?? null,
            p_practice_id: input.practiceId ?? null,
            p_patient_id: input.patientId ?? null,
            p_resident_id: input.residentId ?? null,
            p_category: input.category,
            p_priority: input.priority,
            p_subject: input.subject.trim(),
            p_payload: input.payload,
        });
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        return row ? mapCarePracticeRequestRow(row) : null;
    } catch (e) {
        logger.error("careRequests.create", e);
        return null;
    }
}

/** Alle Anfragen der Einrichtung laden */
export async function getRequestsForFacility(
    facilityId: string,
    options?: { status?: RequestStatus; category?: RequestCategory; limit?: number },
): Promise<CarePracticeRequest[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        let query = supabase
            .from("care_practice_requests")
            .select("*")
            .eq("facility_id", facilityId)
            .order("created_at", { ascending: false });

        if (options?.status) query = query.eq("status", options.status);
        if (options?.category) query = query.eq("category", options.category);
        if (options?.limit) query = query.limit(options.limit);

        const { data, error } = await query;
        if (error) throw error;
        return (data ?? []).map((r: Record<string, unknown>) => mapCarePracticeRequestRow(r));
    } catch (e) {
        logger.error("careRequests.getForFacility", e);
        return [];
    }
}

/** Einzelne Anfrage laden */
export async function getRequest(
    id: string,
    facilityId?: string,
): Promise<CarePracticeRequest | null> {
    if (!isSupabaseConfigured()) return null;
    try {
        let query = supabase.from("care_practice_requests").select("*").eq("id", id);
        if (facilityId) query = query.eq("facility_id", facilityId);

        const { data, error } = await query.single();
        if (error) throw error;
        return data ? mapCarePracticeRequestRow(data as Record<string, unknown>) : null;
    } catch (e) {
        logger.error("careRequests.getRequest", e);
        return null;
    }
}

/** Letzte Anfrage für einen Bewohner laden */
export async function getLatestRequestForResident(
    residentId: string,
    facilityId?: string,
): Promise<CarePracticeRequest | null> {
    if (!isSupabaseConfigured()) return null;
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(residentId)) {
        return null;
    }
    try {
        let query = supabase
            .from("care_practice_requests")
            .select("*")
            .eq("resident_id", residentId)
            .order("updated_at", { ascending: false })
            .order("created_at", { ascending: false })
            .limit(1);

        if (facilityId) {
            query = query.eq("facility_id", facilityId);
        }

        const { data, error } = await query;
        if (error) throw error;

        const [row] = (data ?? []) as Record<string, unknown>[];
        return row ? mapCarePracticeRequestRow(row) : null;
    } catch (e) {
        logger.error("careRequests.getLatestRequestForResident", e);
        return null;
    }
}

/** Status-Statistiken für eigene Anfragen */
export async function getRequestStats(facilityId: string): Promise<RequestStats> {
    const empty: RequestStats = { neu: 0, gesehen: 0, inBearbeitung: 0, rueckfrage: 0, total: 0 };
    if (!isSupabaseConfigured()) return empty;
    try {
        const { data, error } = await supabase
            .from("care_practice_requests")
            .select("status")
            .eq("facility_id", facilityId)
            .neq("status", "ERLEDIGT");
        if (error) throw error;
        return buildCarePracticeRequestStats(data as Record<string, unknown>[] | null | undefined);
    } catch (e) {
        logger.error("careRequests.getStats", e);
        return empty;
    }
}

/** Verknüpfte Praxen laden (für Auswahl-Dropdown) */
export async function getLinkedPractices(
    facilityId: string,
): Promise<Array<{ id: string; name: string; specialty?: string }>> {
    if (!isSupabaseConfigured()) return [];
    await ensureAuthenticatedOptionRead();
    try {
        const [{ data: residentRows, error: residentError }, { data: careResidentRows, error: careResidentError }] = await Promise.all([
            supabase
                .from("residents")
                .select("id")
                .eq("facility_id", facilityId)
                .eq("status", "active"),
            supabase
                .from("care_residents")
                .select("id")
                .eq("facility_id", facilityId)
                .is("deleted_at", null),
        ]);

        if (residentError && careResidentError) {
            throw residentError;
        }

        const seen = new Set<string>();
        const result: Array<{ id: string; name: string; specialty?: string }> = [];

        const residentIds = [...new Set([
            ...((residentRows ?? []).map((row) => (row as Record<string, unknown>).id)),
            ...((careResidentRows ?? []).map((row) => (row as Record<string, unknown>).id)),
        ].filter((value): value is string => typeof value === 'string' && value.length > 0))];

        if (residentIds.length > 0) {
            const { data: authorizedLinks, error: authorizedLinksError } = await supabase
                .rpc('get_care_authorized_practice_links', { p_resident_ids: residentIds });
            if (authorizedLinksError) throw authorizedLinksError;

            for (const link of authorizedLinks ?? []) {
                const row = link as Record<string, unknown>;
                const practiceId = typeof row.practice_id === 'string' ? row.practice_id : null;
                const practiceName = typeof row.practice_name === 'string' ? row.practice_name : null;
                if (!practiceId || !practiceName || seen.has(practiceId)) continue;
                seen.add(practiceId);
                result.push({
                    id: practiceId,
                    name: practiceName,
                });
            }
        }

        if (result.length === 0) {
            const { data: reqData } = await supabase
                .from("care_practice_requests")
                .select("practice_id")
                .eq("facility_id", facilityId)
                .not("practice_id", "is", null);
            const practiceIds = [...new Set((reqData ?? []).map((r: Record<string, unknown>) => r.practice_id as string))];
            if (practiceIds.length > 0) {
                const { data: practices } = await supabase
                    .from("practices")
                    .select("id, name, specialty")
                    .in("id", practiceIds);
                for (const p of practices ?? []) {
                    const pr = p as Record<string, unknown>;
                    const pid = pr.id as string;
                    if (!pid || seen.has(pid)) continue;
                    seen.add(pid);
                    result.push({
                        id: pid,
                        name: (pr.name as string) ?? "Unbekannte Praxis",
                        specialty: pr.specialty as string | undefined,
                    });
                }
            }
        }

        // Strangler-Fig: Partnership-Tabelle als zusätzliche Quelle
        try {
            const partnerships = await getPartners(facilityId);
            for (const p of partnerships) {
                if (p.status !== 'AKTIV') continue;
                const pid = p.targetPracticeId ?? p.requesterPracticeId;
                if (!pid || seen.has(pid)) continue;
                seen.add(pid);
                result.push({
                    id: pid,
                    name: p.partnerName ?? 'Unbekannte Praxis',
                    specialty: p.partnerSpecialty,
                });
            }
        } catch (partnerErr) {
            logger.error('careRequests.getLinkedPractices.partnerFallback', partnerErr);
        }

        return result;
    } catch (e) {
        logger.error("careRequests.getLinkedPractices", e);
        throw e;
    }
}

/** Bewohner der Einrichtung laden (für Auswahl) */
export async function getResidents(
    facilityId: string,
): Promise<Array<{ id: string; name: string; roomNumber?: string }>> {
    if (!isSupabaseConfigured()) return [];
    await ensureAuthenticatedOptionRead();
    try {
        const [{ data: residents, error: residentsError }, { data: careResidents, error: careResidentsError }] = await Promise.all([
            supabase
                .from("residents")
                .select("id, first_name, last_name, room_number")
                .eq("facility_id", facilityId)
                .eq("status", "active")
                .order("last_name", { ascending: true }),
            supabase
                .from("care_residents")
                .select("id, first_name, last_name, room_number")
                .eq("facility_id", facilityId)
                .is("deleted_at", null)
                .order("last_name", { ascending: true }),
        ]);

        if (residentsError && careResidentsError) throw residentsError;

        const seen = new Set<string>();
        return [...(residents ?? []), ...(careResidents ?? [])]
            .map((r: Record<string, unknown>) => ({
                id: r.id as string,
                name: `${r.last_name ?? ""}, ${r.first_name ?? ""}`.trim(),
                roomNumber: r.room_number as string | undefined,
            }))
            .filter((resident) => {
                if (!resident.id || seen.has(resident.id)) {
                    return false;
                }
                seen.add(resident.id);
                return true;
            });
    } catch (e) {
        logger.error("careRequests.getResidents", e);
        throw e;
    }
}

/** Care-Seite: Antwort auf Rückfrage oder Kommentar senden */
export async function replyToRequest(
    requestId: string,
    comment: string,
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { error } = await sb.rpc("reply_care_practice_request", {
            p_request_id: requestId,
            p_comment: comment.trim(),
        });
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("careRequests.replyToRequest", e);
        return false;
    }
}

/** Realtime-Subscription auf Statusänderungen */
export function subscribeToRequests(
    facilityId: string,
    onUpdate: () => void,
): { unsubscribe: () => void } {
    if (!isSupabaseConfigured()) return { unsubscribe: () => {} };

    return createCarePracticeRequestSubscription({
        client: supabase,
        scope: "facility",
        scopeId: facilityId,
        onEvent: ({ channelName, filter, payload, table }) => {
            const payloadNew = payload.new ?? null;
            const payloadOld = payload.old ?? null;
            recordRealtimeDebug({
                channelName,
                eventType: payload.eventType ?? null,
                filter,
                kind: "event",
                newId: typeof payloadNew?.id === "string" ? payloadNew.id : null,
                newStatus: typeof payloadNew?.status === "string" ? payloadNew.status : null,
                oldId: typeof payloadOld?.id === "string" ? payloadOld.id : null,
                oldStatus: typeof payloadOld?.status === "string" ? payloadOld.status : null,
                table,
            });
            logger.debug("careRequests.realtime.event", {
                channelName,
                eventType: payload.eventType ?? null,
                filter,
                newId: typeof payloadNew?.id === "string" ? payloadNew.id : null,
                newStatus: typeof payloadNew?.status === "string" ? payloadNew.status : null,
                oldId: typeof payloadOld?.id === "string" ? payloadOld.id : null,
                oldStatus: typeof payloadOld?.status === "string" ? payloadOld.status : null,
                table,
            });
        },
        onStatus: ({ channelName, error, status }) => {
            recordRealtimeDebug({ channelName, error, kind: "status", status });
            logger.info("careRequests.realtime.status", { channelName, error, status });
        },
        onUpdate,
    });
}

export function subscribeToRequestDetail(
    requestId: string,
    onUpdate: () => void,
): { unsubscribe: () => void } {
    if (!isSupabaseConfigured()) return { unsubscribe: () => {} };

    const channelName = `care-request-detail:${requestId}`;
    const filter = `id=eq.${requestId}`;

    const channel = supabase
        .channel(channelName)
        .on(
            "postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "care_practice_requests",
                filter,
            },
            (payload) => {
                const payloadNew = payload.new && typeof payload.new === "object"
                    ? payload.new as Record<string, unknown>
                    : null;
                const payloadOld = payload.old && typeof payload.old === "object"
                    ? payload.old as Record<string, unknown>
                    : null;
                recordRealtimeDebug({
                    channelName,
                    eventType: payload.eventType ?? null,
                    filter,
                    kind: "detail-event",
                    newId: typeof payloadNew?.id === "string" ? payloadNew.id : null,
                    newStatus: typeof payloadNew?.status === "string" ? payloadNew.status : null,
                    oldId: typeof payloadOld?.id === "string" ? payloadOld.id : null,
                    oldStatus: typeof payloadOld?.status === "string" ? payloadOld.status : null,
                    table: "care_practice_requests",
                });
                logger.debug("careRequests.realtime.detail.event", {
                    channelName,
                    eventType: payload.eventType ?? null,
                    filter,
                    newId: typeof payloadNew?.id === "string" ? payloadNew.id : null,
                    newStatus: typeof payloadNew?.status === "string" ? payloadNew.status : null,
                    oldId: typeof payloadOld?.id === "string" ? payloadOld.id : null,
                    oldStatus: typeof payloadOld?.status === "string" ? payloadOld.status : null,
                    table: "care_practice_requests",
                });
                onUpdate();
            },
        );

    const subscription = channel.subscribe((status, err) => {
        recordRealtimeDebug({ channelName, error: err?.message ?? null, kind: "detail-status", status });
        logger.info("careRequests.realtime.detail.status", {
            channelName,
            error: err?.message ?? null,
            status,
        });
    });

    return {
        unsubscribe: () => {
            supabase.removeChannel(subscription);
        },
    };
}

// ── Status-Transitions (Care als Empfänger oder Sender) ────

/** Care-Seite: Status-Transition via RPC (Dual-Role: Care als Sender oder Empfänger) */
export async function updateRequestByCare(
    id: string,
    input: { command: string; lastComment?: string; resolutionNotes?: string },
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { error } = await sb.rpc("transition_care_practice_request", {
            p_request_id: id,
            p_command: input.command,
            p_comment: input.lastComment ?? null,
            p_resolution_notes: input.resolutionNotes ?? null,
        });
        if (error) {
            logger.error("updateRequestByCare failed", { id, command: input.command, error: error.message });
            return false;
        }
        return true;
    } catch (e) {
        logger.error("updateRequestByCare", e);
        return false;
    }
}

// ── Timeline ────────────────────────────────────────────────

/** Lade die vollständige Konversations-Timeline einer Anfrage */
export async function getRequestTimeline(requestId: string): Promise<RequestTimelineEntry[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_care_practice_request_timeline", {
            p_request_id: requestId,
        });
        if (error) {
            logger.error("getRequestTimeline failed", { requestId, error: error.message });
            return [];
        }
        return (data ?? []).map((row: Record<string, unknown>) => mapTimelineEntry(row));
    } catch (e) {
        logger.error("getRequestTimeline", e);
        return [];
    }
}

// ── Link-Status (Care-Perspektive) ──────────────────────────

export interface CarePracticeLinkStatus {
    exists: boolean;
    linkId: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

interface CarePracticeLinkStatusRow {
    id: string | null;
    created_at: string | null;
    updated_at: string | null;
    expires_at: string | null;
    is_active: boolean | null;
    status: string | null;
    source_facility_id: string | null;
    target_facility_id: string | null;
    source_practice_id: string | null;
    target_practice_id: string | null;
}

/** Prüfe ob ein aktiver Care-Practice-Link für Resident↔Patient existiert */
export async function getCarePracticeLinkStatus(params: {
    residentId: string;
    patientId: string;
    facilityId: string;
    practiceId: string;
}): Promise<CarePracticeLinkStatus> {
    const emptyState: CarePracticeLinkStatus = {
        exists: false,
        linkId: null,
        createdAt: null,
        updatedAt: null,
    };

    if (!isSupabaseConfigured()) return emptyState;

    try {
        const sb = requireSupabase();
        const { data, error } = await sb
            .from("cross_sector_data_sharing")
            .select([
                "id",
                "created_at",
                "updated_at",
                "expires_at",
                "is_active",
                "status",
                "source_facility_id",
                "target_facility_id",
                "source_practice_id",
                "target_practice_id",
            ].join(", "))
            .eq("resident_id", params.residentId)
            .eq("patient_id", params.patientId)
            .eq("is_active", true);

        if (error) throw error;

        const linkRows = (data ?? []) as unknown as CarePracticeLinkStatusRow[];
        const now = Date.now();
        const activeLink = linkRows.find((row) => {
            const facilityId = typeof row.source_facility_id === "string"
                ? row.source_facility_id
                : typeof row.target_facility_id === "string"
                    ? row.target_facility_id
                    : null;
            const practiceId = typeof row.source_practice_id === "string"
                ? row.source_practice_id
                : typeof row.target_practice_id === "string"
                    ? row.target_practice_id
                    : null;

            if (facilityId !== params.facilityId) return false;
            if (practiceId !== params.practiceId) return false;
            if (typeof row.status === "string" && row.status !== "active") return false;
            if (row.expires_at == null) return true;
            if (typeof row.expires_at !== "string") return false;

            const expiresAtTimestamp = Date.parse(row.expires_at);
            return Number.isNaN(expiresAtTimestamp) || expiresAtTimestamp > now;
        });

        if (!activeLink) return emptyState;

        return {
            exists: true,
            linkId: (activeLink.id as string) ?? null,
            createdAt: (activeLink.created_at as string) ?? null,
            updatedAt: (activeLink.updated_at as string) ?? null,
        };
    } catch (e) {
        logger.error("getCarePracticeLinkStatus", e);
        return emptyState;
    }
}
